import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const SearchPage = () => {
  const [searchType, setSearchType] = useState('admin');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [activeGraduationDay, setActiveGraduationDay] = useState(null);

  useEffect(() => {
    fetchActiveGraduationDay();
  }, []);

  const fetchActiveGraduationDay = async () => {
    try {
      const response = await api.get('/graduation-days/active');
      setActiveGraduationDay(response.data);
    } catch (error) {
      console.error('Error fetching active graduation day:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) {
      setError('Please enter a search value');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await api.get(`/graduates/search?query=${encodeURIComponent(searchValue)}`);
      if (response.data) {
        setResults(response.data);
      } else {
        setError('No graduate found with the provided information');
      }
    } catch (error) {
      console.error('Search error:', error);
      if (error.response?.status === 404) {
        setError('No graduate found with the provided information');
      } else if (error.response?.status === 400) {
        setError('Please enter a valid search value');
      } else {
        setError('An error occurred while searching. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };

  const getGraduationStatus = () => {
    if (!results) return { text: '—', class: 'text-gray-600', icon: '' };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let ceremonyDate = null;

    if (results.ceremony_date) {
      ceremonyDate = new Date(results.ceremony_date);
      ceremonyDate.setHours(0, 0, 0, 0);
    } else if (activeGraduationDay && activeGraduationDay.ceremony_date) {
      ceremonyDate = new Date(activeGraduationDay.ceremony_date);
      ceremonyDate.setHours(0, 0, 0, 0);
    }

    if (ceremonyDate) {
      if (today < ceremonyDate) {
        return { text: 'Graduating', class: 'text-blue-600 font-semibold', icon: '🎓' };
      } else if (today >= ceremonyDate) {
        return { text: 'Graduated', class: 'text-green-600 font-semibold', icon: '✓' };
      }
    }

    if (results.graduation_year) {
      const gradYear = parseInt(results.graduation_year);
      const currentYear = today.getFullYear();

      if (gradYear > currentYear) {
        return { text: 'Graduating', class: 'text-blue-600 font-semibold', icon: '🎓' };
      } else if (gradYear < currentYear) {
        return { text: 'Graduated', class: 'text-green-600 font-semibold', icon: '✓' };
      } else {
        const currentMonth = today.getMonth();
        if (currentMonth >= 5) {
          return { text: 'Graduated', class: 'text-green-600 font-semibold', icon: '✓' };
        } else {
          return { text: 'Graduating', class: 'text-blue-600 font-semibold', icon: '🎓' };
        }
      }
    }

    return { text: 'Graduated', class: 'text-green-600 font-semibold', icon: '✓' };
  };

  const getCourseDisplay = () => {
    if (!results) return '—';

    let courseText = results.course_name || '';

    if (results.level_number) {
      courseText += ` (Level ${results.level_number})`;
    } else if (results.level_name) {
      courseText += ` (${results.level_name})`;
    }

    return courseText;
  };

  const getGradeBadgeClass = (grade) => {
    switch(grade) {
      case 'Distinction':
        return 'bg-yellow-100 text-yellow-800';
      case 'Credit':
        return 'bg-blue-100 text-blue-800';
      case 'Pass':
        return 'bg-green-100 text-green-800';
      case 'Mastery':
        return 'bg-purple-100 text-purple-800';
      case 'Proficiency':
        return 'bg-indigo-100 text-indigo-800';
      case 'Competent':
        return 'bg-cyan-100 text-cyan-800';
      case 'First Class':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const status = getGraduationStatus();

  return (
    <div style={{ backgroundColor: '#FEFBEC', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div className="bg-white border-b-4 border-[#fbbf24] shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-xl">🔍</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800 mb-0.5">
                Search Graduation Records
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Enter your Admission Number or Telephone Number to verify your graduation status
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Card className="mb-6">
            <form onSubmit={handleSearch}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search By
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="admin"
                      checked={searchType === 'admin'}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">Admission Number</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="telephone"
                      checked={searchType === 'telephone'}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">Telephone Number</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {searchType === 'admin'
                    ? 'Format: DEE/001/S2021 or any valid admin number'
                    : 'Format: 07XXXXXXXX or 2547XXXXXXXX'}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    label=""
                    type="text"
                    placeholder={searchType === 'admin' ? 'e.g., DEE/001/S2021' : 'e.g., 0712345678'}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    required
                    className="w-full"
                    style={{ height: '42px' }}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="whitespace-nowrap"
                  style={{ height: '42px', display: 'inline-flex', alignItems: 'center' }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </form>
          </Card>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {results && (
            <Card className="mt-4">
              <div className="border-b pb-3 mb-3">
                <h2 className="text-lg font-bold text-gray-800">Graduate Details</h2>
                <p className="text-xs text-green-600 mt-1">✓ Verified Graduate</p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Full Name:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">{results.name}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Admin Number:</span>
                  <span className="text-gray-900 font-mono sm:w-2/3 break-words">{results.admin_number}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Telephone:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">{results.telephone || '—'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Email:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">{results.email || '—'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Department:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">{results.department_name}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Course:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">{getCourseDisplay()}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Exam Body:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">{results.exam_body_name || '—'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Graduation Year:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">{results.graduation_year || '—'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Graduation Date:</span>
                  <span className="text-gray-900 sm:w-2/3 break-words">
                    {results.ceremony_date ? formatDate(results.ceremony_date) :
                     activeGraduationDay?.ceremony_date ? formatDate(activeGraduationDay.ceremony_date) : '—'}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Grade:</span>
                  <span className="sm:w-2/3">
                    <span className={`px-2 py-1 inline-block rounded text-xs ${getGradeBadgeClass(results.grade)}`}>
                      {results.grade || 'N/A'}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between py-2">
                  <span className="font-semibold text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Status:</span>
                  <span className={`sm:w-2/3 ${status.class}`}>
                    {status.icon} {status.text}
                  </span>
                </div>
              </div>

              {status.text === 'Graduating' && (results.ceremony_date || activeGraduationDay?.ceremony_date) && (
                <div className="mt-4 pt-3 border-t">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-sm text-blue-800">
                      🎓 Your graduation ceremony will be held on{' '}
                      <strong>
                        {results.ceremony_date ? formatDate(results.ceremony_date) :
                         activeGraduationDay?.ceremony_date ? formatDate(activeGraduationDay.ceremony_date) : '—'}
                      </strong>
                      {activeGraduationDay?.venue && ` at ${activeGraduationDay.venue}`}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-4 pt-3 border-t text-center">
                <p className="text-xs text-gray-500">Official record from Katine TTI</p>
              </div>
            </Card>
          )}

          <div className="mt-5 text-center">
            <p className="text-xs text-gray-500">
              Need help? Contact the registrar's office at{' '}
              <a href="mailto:registrar@katinetti.ac.ke" className="text-primary hover:underline">
                registrar@katinetti.ac.ke
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
